import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Label, Icon } from 'semantic-ui-react';
import { DatePicker } from '../shared/DatePicker';
import WeekDaysSelect from '../shared/WeekDaysSelect';
import PropTypes from 'prop-types';
import HotelsDropDown from '../shared/HotelsDropDown';
import { useMessageResources, useTextResources } from '../../language';
import moment from 'moment';
import { isMobileOnly, isBrowser } from 'mobile-device-detect';
import { useLoader } from '../../hooks/useLoader';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions } from '../shared/permissionsActions';
import { serverDateFormat } from '../shared/constants';
import { onError, onSuccess, onWarning } from '../shared/messages';
import { Live } from '../../api/services/next';
import { getDaysOfDatesRange, toServerDateFormat } from '../shared/utils';
import { useConfirm } from '../../hooks/useConfirm';

const RemoveManualRestrictionsPopup = ({
    onClose,
    onSave,
    HotelID,
    controller,
    startOpen,
    initialDate,
    ...rest
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();
    const today = moment().format(serverDateFormat);
    const getSeedDates = () => {
        if (!initialDate) return [today, today];
        const clamped = toServerDateFormat(moment.max(moment(initialDate), moment().startOf('day')));
        return [clamped, clamped];
    };
    const [dates, setDates] = useState(getSeedDates);
    const [open, setOpen] = useState(startOpen);
    const [days, setDays] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(HotelID);
    const [selectedDates, setSelectedDates] = useState([]);

    const saveAction = async (close) => {
        if (!selectedDates.length) {
            onWarning(text('WarningNoDatesSelected'));
            return;
        }

        const isConfirmed = await confirm({
            content: message('ConfirmRemoveManualRestrictions'),
            confirmButton: {
                content: text('Continue'),
                positive: true,
            },
            cancelButton: {
                content: text('Cancel'),
                negative: true,
            },
        })

        if (!isConfirmed) {
            return;
        }

        setIsSaving(true);
        loader(true);
        
        try {
            const dateRanges = selectedDates
                .map(({ startDate, endDate }) => getDaysOfDatesRange(startDate, endDate, days))
                .flat()
                .sort((d1, d2) => d1.isBefore(d2) ? -1 : 1)
                .reduce((ranges, date) => {
                    if (ranges.length === 0) {
                        ranges.push([date, date]);
                    } else {
                        const lastRange = ranges[ranges.length - 1];
                        const lastDateInRange = lastRange[1];
                        if (date.diff(lastDateInRange, 'days') === 1) {
                            lastRange[1] = date;
                        } else if (!date.isSame(lastDateInRange, 'day')) {
                            ranges.push([date, date]);
                        }
                    }
                    return ranges;
                }, [])
                .map(range => range.map(toServerDateFormat));
                
            const result = await Live.RemoveManualRestrictions({
                HotelID: selectedHotel,
                DateRanges: dateRanges
            });
            
            if (onSave) {
                onSave(result);
            }
            onSuccess(text('ManualRestrictionsRemovedSuccessfully'), { count: result });
            
            if (close) {
                closeAction();
            }
        } catch (error) {
            console.error(error);
            if (error.status === 401) {
                onWarning(text('Unauthorized'));
            }
            else if (error.status === 403) {
                onWarning(message('NoPermissionsForThisAction'));
            }
            else if (error.status < 500) {
                onWarning(error.title);
            }
            else {
                onError(error.title);
            }
        }
        finally {
            loader(false);
            setIsSaving(false);
        }
    };

    const closeAction = () => {
        setOpen(false);
        resetFields();
        onClose?.();
    };

    useEffect(() => {
        if (!open) {
            resetFields();
        }
    }, [open]);

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue);
        } else {
            setDates(getSeedDates());
        }
    };

    const addDateToList = () => {
        if (
            selectedDates.some(
                (d) =>
                    moment(d.startDate).isSame(dates[0], 'day') &&
                    moment(d.endDate).isSame(dates[1], 'day')
            )
        ) {
            onWarning(text('WarningDuplicateDates'));
            return;
        }
        setSelectedDates((selectedDates) =>
            selectedDates.concat({
                startDate: dates[0],
                endDate: dates[1],
            })
        );
    };

    const onRemoveDateFromSelectedDates = (index, e) => {
        if (e) {
            setSelectedDates((selectedDates) =>
                selectedDates.filter((_, i) => i !== index)
            );
        }
    };

    const resetFields = () => {
        setDates(getSeedDates());
        setDays(null);
        setSelectedHotel(HotelID);
        setSelectedDates([]);
    };

    const isSaveDisabled = () => {
        return isSaving || !selectedHotel;
    };

    const memoDates = useMemo(() => {
        return selectedDates.length
            ? selectedDates
            : [{ startDate: dates[0], endDate: dates[1] }];
    }, [dates, selectedDates]);

    return (
        <ValidateModalPermission
            open={open}
            onClose={onClose}
            actionId={PermissionsActions.RemoveManualRestrictions}
        >
            <Modal
                size={isMobileOnly ? 'fullscreen' : 'tiny'}
                className={isMobileOnly ? 'mobile' : ''}
                open={open}
                style={{ zIndex: 1000000 }}
                {...rest}
            >
                {!isMobileOnly && (
                    <Modal.Header>{text('RemoveManualRestrictions')}</Modal.Header>
                )}
                <Modal.Content
                    style={
                        isMobileOnly
                            ? { overflowY: 'auto' }
                            : { minHeight: 450, maxHeight: 600, overflowY: 'auto' }
                    }
                >
                    <Form size='small'>
                        <Form.Group
                            unstackable
                            widths={'equal'}
                            style={{ flexWrap: 'wrap' }}
                        >
                            {selectedDates.map((selectedDate, index) => (
                                <Label.Group key={index}>
                                    <Label as='a'>
                                        {moment(selectedDate.startDate).format('DD/MM/YYYY')}-
                                        {moment(selectedDate.endDate).format('DD/MM/YYYY')}
                                        <Icon
                                            key={index}
                                            name={'delete'}
                                            onClick={onRemoveDateFromSelectedDates.bind(this, index)}
                                        />
                                    </Label>
                                </Label.Group>
                            ))}
                        </Form.Group>
                        <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
                            <Form.Field width={12} style={{ display: 'grid' }}>
                                <DatePicker
                                    clearable={false}
                                    picktype='range'
                                    onChange={handleDatesChanged}
                                    label='Date Range'
                                    value={dates}
                                    minDate={moment().startOf('day').toDate()}
                                />
                            </Form.Field>
                            <Form.Field widths={4}>
                                <Button size='small' primary onClick={addDateToList} data-testid="add-date-button">
                                    {text('AddDate')}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                        <Form.Field
                            label={text('Weekdays')}
                            control={WeekDaysSelect}
                            onChange={setDays}
                            dates={memoDates}
                        />
                        <Form.Field
                            label={text("Hotel")}
                            required
                            control={HotelsDropDown}
                            onChange={setSelectedHotel}
                            value={selectedHotel}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {isBrowser && (
                        <Button
                            size={isMobileOnly ? 'tiny' : 'small'}
                            disabled={isSaving}
                            negative
                            onClick={closeAction}
                        >
                            {text('Close')}
                        </Button>
                    )}
                    {isBrowser && (
                        <Button
                            size={'small'}
                            onClick={resetFields}
                            data-testid="reset-button"
                        >
                            {text('Reset')}
                        </Button>
                    )}
                    {isBrowser && (
                        <Button
                            size={isMobileOnly ? 'tiny' : 'small'}
                            disabled={isSaveDisabled()}
                            loading={isSaving}
                            positive
                            onClick={() => saveAction(true)}
                            data-testid="save-and-close-button"
                        >
                            {text('Saveandclose')}
                        </Button>
                    )}
                    <Button
                        size={isMobileOnly ? 'tiny' : 'small'}
                        disabled={isSaveDisabled()}
                        loading={isSaving}
                        positive
                        onClick={() => saveAction(false)}
                        data-testid="save-button"
                    >
                        {text('Save')}
                    </Button>
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    );
};

RemoveManualRestrictionsPopup.propTypes = {
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    HotelID: PropTypes.any,
    controller: PropTypes.string,
    startOpen: PropTypes.bool,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default RemoveManualRestrictionsPopup;
